import { BorderedContainer } from 'core/components/common/BorderedContainer';
import { SectionTitle } from 'core/components/common/SectionTitle';
import { FC } from 'react';
import { IncidentDetailPatient } from '../useIncidentDetailForHiroshima';
import { InfoDisplay } from 'core/components/JianDetail/common/InfoDisplay';
import { convertToPresenceAbsence } from 'core/components/common/converters';
import { formatDateTime } from 'core/utils/date';

type Props = {
  isCpa: boolean;
  data: IncidentDetailPatient['cpaData'];
};

export const CPAModeView: FC<Props> = ({ isCpa, data }) => {
  if (!isCpa) {
    return (
      <BorderedContainer>
        <div className="flex flex-col gap-[24px]">
          <SectionTitle text="CPAモード" />
          <p>CPA事案ではありません</p>
        </div>
      </BorderedContainer>
    );
  }

  if (!data) {
    return (
      <BorderedContainer>
        <div className="flex flex-col gap-[24px]">
          <SectionTitle text="CPAモード" />
          <p>CPAデータが存在しません</p>
        </div>
      </BorderedContainer>
    );
  }

  return (
    <BorderedContainer>
      <div className="flex flex-col gap-[24px]">
        <SectionTitle text="CPAモード" />
        <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
          <InfoDisplay
            title="目撃"
            content={convertToPresenceAbsence(data.hasCpa)}
          />
          <InfoDisplay
            title="最終健常確認日時"
            content={
              data.lastHealthedAt
                ? formatDateTime(data.lastHealthedAt, 'MM/dd HH:mm')
                : ''
            }
          />
          <InfoDisplay title="初期波形" content={data.initialEcg} />
        </div>
        <SectionTitle size="medium" variant="default" text="接触時" />
        <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
          <InfoDisplay
            title="死戦期呼吸"
            content={convertToPresenceAbsence(
              data.contactData?.hasAgonalRespiration
            )}
          />
          <InfoDisplay
            title="対光反射"
            content={data.contactData?.pupilsReaction}
          />
          <InfoDisplay
            title="体動"
            content={convertToPresenceAbsence(data.contactData?.hasMovement)}
          />
        </div>
        <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
          <InfoDisplay
            title="瞳孔径(左)"
            content={
              data.contactData?.pupilSizeLeft
                ? `${data.contactData?.pupilSizeLeft} mm`
                : ''
            }
          />
          <InfoDisplay
            title="瞳孔径(右)"
            content={
              data.contactData?.pupilSizeRight
                ? `${data.contactData?.pupilSizeRight} mm`
                : ''
            }
          />
          <InfoDisplay
            title="瞳孔散大"
            content={convertToPresenceAbsence(
              data.contactData?.hasDilatedPupils
            )}
          />
          <InfoDisplay
            title="脈触知"
            content={convertToPresenceAbsence(
              data.contactData?.hasDetectablePulse
            )}
          />
        </div>
        <SectionTitle size="medium" variant="default" text="搬出時" />
        <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
          <InfoDisplay
            title="脈触知"
            content={convertToPresenceAbsence(
              data.extricationData?.hasDetectablePulse
            )}
          />
        </div>
        <SectionTitle size="medium" variant="default" text="救急車収容時" />
        <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
          <InfoDisplay
            title="死戦期呼吸"
            content={convertToPresenceAbsence(
              data.accommodationData.hasAgonalRespiration
            )}
          />
          <InfoDisplay
            title="対光反射"
            content={data.accommodationData.pupilsReaction}
          />
          <InfoDisplay
            title="体動"
            content={convertToPresenceAbsence(
              data.accommodationData.hasMovement
            )}
          />
        </div>
        <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
          <InfoDisplay
            title="瞳孔径(左)"
            content={
              data.accommodationData.pupilSizeLeft
                ? `${data.accommodationData.pupilSizeLeft} mm`
                : ''
            }
          />
          <InfoDisplay
            title="瞳孔径(右)"
            content={
              data.accommodationData.pupilSizeRight
                ? `${data.accommodationData.pupilSizeRight} mm`
                : ''
            }
          />
          <InfoDisplay
            title="瞳孔散大"
            content={convertToPresenceAbsence(
              data.accommodationData.hasDilatedPupils
            )}
          />
          <InfoDisplay
            title="脈触知"
            content={convertToPresenceAbsence(
              data.accommodationData.hasDetectablePulse
            )}
          />
        </div>
        <SectionTitle size="medium" variant="default" text="搬送中" />
        <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
          <InfoDisplay
            title="脈触知"
            content={convertToPresenceAbsence(
              data.destinationData?.hasDetectablePulse
            )}
          />
        </div>
        <SectionTitle size="medium" variant="default" text="病院到着時" />
        <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
          <InfoDisplay
            title="死戦期呼吸"
            content={convertToPresenceAbsence(
              data.hospitalArrivalData.hasAgonalRespiration
            )}
          />
          <InfoDisplay
            title="対光反射"
            content={data.hospitalArrivalData.pupilsReaction}
          />
          <InfoDisplay
            title="体動"
            content={convertToPresenceAbsence(
              data.hospitalArrivalData.hasMovement
            )}
          />
        </div>
        <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
          <InfoDisplay
            title="瞳孔径(左)"
            content={
              data.hospitalArrivalData.pupilSizeLeft
                ? `${data.hospitalArrivalData.pupilSizeLeft} mm`
                : ''
            }
          />
          <InfoDisplay
            title="瞳孔径(右)"
            content={
              data.hospitalArrivalData.pupilSizeRight
                ? `${data.hospitalArrivalData.pupilSizeRight} mm`
                : ''
            }
          />
          <InfoDisplay
            title="瞳孔散大"
            content={convertToPresenceAbsence(
              data.hospitalArrivalData.hasDilatedPupils
            )}
          />
          <InfoDisplay
            title="脈触知"
            content={convertToPresenceAbsence(
              data.hospitalArrivalData.hasDetectablePulse
            )}
          />
        </div>
      </div>
    </BorderedContainer>
  );
};
